import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  sendUserAnswersAction,
  setAnswersAction,
} from 'root-redux/actions/user'
import { selectAppName } from 'root-redux/selects/common'
import {
  selectIsUpgradePurchased,
  selectUUID,
  selectUserSubscriptionInfo,
} from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useCompatibleRetentionFlowPaymentMethod } from 'hooks/useCompatibleRetentionFlowPaymentMethod'

import { getSubscriptionUIStatus } from 'helpers/getSubscriptionUIStatus'

import { Events, eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import { Language, READABLE_APP_NAMES, UnsubscribeReason } from 'root-constants'

import { FemiButton } from '../../components/FemiButton'
import { FemiFeedbackPopup } from '../../components/FemiFeedbackPopup'
import { FemiOption } from '../../components/FemiOption'
import { FemiPageContainer } from '../../components/FemiPageContainer'
import { useCancelSubscription } from '../../hooks/useCancelSubscription'
import { FemiBottomArea, FemiContent, FemiOptions } from '../../styles'
import { StyledFemiReasons as S } from './FemiReasons.styles'
import { FEMI_REASON_VALUES } from './constants'

export const FemiReasons: React.FC = () => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const appName = useSelector(selectAppName)
  const userId = useSelector(selectUUID)
  const isUpgradePurchased = useSelector(selectIsUpgradePurchased)
  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const isCompatibleRetentionFlowPaymentMethod =
    useCompatibleRetentionFlowPaymentMethod()

  const isAnsweredRef = useRef(false)
  const {
    isFeedbackPopupShown,
    closeFeedbackPopup,
    cancelSubscription,
    goToCancellation,
    goToPauseOffer,
  } = useCancelSubscription()

  const { isPausedSubscription } = getSubscriptionUIStatus(
    userSubscriptionInfo?.uiStatus,
  )

  const goToNextStep = (answer: UnsubscribeReason) => {
    if (isPausedSubscription || !isCompatibleRetentionFlowPaymentMethod) {
      goToCancellation()
      return
    }

    if (answer === UnsubscribeReason.TECH_ISSUES) {
      goTo({ pathname: PageId.TECHNICAL_ISSUES, search })
      return
    }

    if (answer === UnsubscribeReason.TOO_EXPENSIVE && !isUpgradePurchased) {
      goTo({ pathname: PageId.DISCOUNT_OFFER, search })
      return
    }

    goToPauseOffer()
  }

  const handleAnswer = (answer: UnsubscribeReason) => {
    if (isAnsweredRef.current) return

    isAnsweredRef.current = true
    dispatch(setAnswersAction({ answers: answer, pageId: PageId.REASONS }))
    dispatch(sendUserAnswersAction())

    eventLogger.logQuestion({
      event: Events.UNSUB_FLOW_QUESTION_2_END,
      question: t('retention.notContinueReasons.title', {
        appName: READABLE_APP_NAMES[appName],
        lng: Language.EN,
      }),
      answers: answer,
      userId,
    })

    goToNextStep(answer)
  }

  const handleFeedbackPopupClose = () => {
    isAnsweredRef.current = false
    closeFeedbackPopup()
  }

  return (
    <FemiPageContainer hasNavigation>
      <S.Content>
        <FemiContent>
          <S.Title>{t('retention.femi.reasons.title')}</S.Title>
          <FemiOptions>
            {FEMI_REASON_VALUES.map((value) => (
              <FemiOption key={value} onClick={() => handleAnswer(value)}>
                {t(`retention.femi.reasons.answers.${value}`)}
              </FemiOption>
            ))}
          </FemiOptions>
        </FemiContent>
      </S.Content>
      <FemiBottomArea isFixed>
        <FemiButton
          type="button"
          variant="secondary"
          onClick={() => handleAnswer(UnsubscribeReason.NO_REASON)}
        >
          {t('retention.femi.skip')}
        </FemiButton>
      </FemiBottomArea>
      <FemiFeedbackPopup
        isShown={isFeedbackPopupShown}
        onComplete={cancelSubscription}
        onClose={handleFeedbackPopupClose}
      />
    </FemiPageContainer>
  )
}
